import type { VisualStyle } from '../game/visualStyles.js';
import { isDarkTheme } from '../theme.js';

export interface SceneTheme3D {
  background: number;
  fogColor: number;
  fogDensity: number;
  tileEmpty: number;
  tileFilled: number;
  tileShades: number[];
  ambientColor: number;
  ambientIntensity: number;
  directionalIntensity: number;
  fillColor: number;
  fillIntensity: number;
  railColor: number;
  wallBrass: number;
  wallSteel: number;
  coreColor: number;
  coreOpacity: number;
  coreGlowColor: number;
}

function cssVar(name: string, fallback: string): string {
  const value = getComputedStyle(document.documentElement).getPropertyValue(name).trim();
  return value || fallback;
}

function cssColorToHex(color: string): number {
  const trimmed = color.trim();
  if (trimmed.startsWith('#')) {
    const hex = trimmed.slice(1);
    if (hex.length === 3) {
      const r = parseInt(hex[0] + hex[0], 16);
      const g = parseInt(hex[1] + hex[1], 16);
      const b = parseInt(hex[2] + hex[2], 16);
      return (r << 16) | (g << 8) | b;
    }
    if (hex.length >= 6) {
      return parseInt(hex.slice(0, 6), 16);
    }
  }

  const rgbMatch = trimmed.match(/rgba?\(\s*([\d.]+)[,\s]+([\d.]+)[,\s]+([\d.]+)/i);
  if (rgbMatch) {
    const r = Math.round(Number(rgbMatch[1]));
    const g = Math.round(Number(rgbMatch[2]));
    const b = Math.round(Number(rgbMatch[3]));
    return (r << 16) | (g << 8) | b;
  }

  return 0x888888;
}

export function getSceneTheme3D(visualStyle: VisualStyle = 'pipe'): SceneTheme3D {
  const dark = isDarkTheme();
  const gear = visualStyle === 'gear';

  if (dark) {
    return {
      background: cssColorToHex(
        cssVar(gear ? '--gear-scene-bg' : '--scene-3d-bg', gear ? '#0a0a12' : '#08080f'),
      ),
      fogColor: cssColorToHex(
        cssVar(gear ? '--gear-scene-bg-stop' : '--scene-3d-fog', gear ? '#12121c' : '#08080f'),
      ),
      fogDensity: gear ? 0.05 : 0.04,
      tileEmpty: cssColorToHex(
        cssVar(gear ? '--gear-steel' : '--canvas-cell-empty', gear ? '#6a6a78' : '#14141c'),
      ),
      tileFilled: gear
        ? cssColorToHex(cssVar('--gear-steel-highlight', '#8a8a98'))
        : 0x1e1e2a,
      tileShades: gear ? [0x000000, 0x040404, 0x080808, 0x0c0c0c] : [0x000000, 0x060606, 0x0c0c0c, 0x131313],
      ambientColor: gear ? 0x303040 : 0x404060,
      ambientIntensity: gear ? 0.35 : 0.45,
      directionalIntensity: gear ? 0.85 : 1.1,
      fillColor: gear ? 0x8a6a3a : 0x6366f1,
      fillIntensity: gear ? 0.12 : 0.25,
      railColor: cssColorToHex(cssVar('--gear-rail', gear ? '#2a2a34' : '#2a2a34')),
      wallBrass: cssColorToHex(cssVar('--gear-wall-brass', gear ? '#8a6a3a' : '#a8845a')),
      wallSteel: cssColorToHex(cssVar('--gear-wall-steel', gear ? '#555568' : '#9090a0')),
      coreColor: gear ? 0x4a3a2a : 0x2a2a4a,
      coreOpacity: 0.5,
      coreGlowColor: gear ? 0xc89a5a : 0x8896ff,
    };
  }

  return {
    background: cssColorToHex(
      cssVar(gear ? '--gear-scene-bg' : '--scene-3d-bg', gear ? '#e8eaf0' : '#eef0f8'),
    ),
    fogColor: cssColorToHex(
      cssVar(gear ? '--gear-scene-bg-stop' : '--scene-3d-fog', gear ? '#f5f6fa' : '#eef0f8'),
    ),
    fogDensity: gear ? 0.015 : 0.012,
    tileEmpty: cssColorToHex(
      cssVar(gear ? '--gear-steel' : '--canvas-cell-empty', gear ? '#a8a8b4' : '#f0f0f5'),
    ),
    tileFilled: gear
      ? cssColorToHex(cssVar('--gear-steel-highlight', '#c0c0cc'))
      : cssColorToHex(cssVar('--canvas-bg-stop', '#ffffff')),
    tileShades: gear ? [0x000000, 0x020202, 0x040404, 0x060606] : [0x000000, 0x030303, 0x060606, 0x090909],
    ambientColor: gear ? 0xe8e8f0 : 0xffffff,
    ambientIntensity: gear ? 0.65 : 0.72,
    directionalIntensity: gear ? 0.8 : 0.95,
    fillColor: gear ? 0xa8845a : 0x818cf8,
    fillIntensity: gear ? 0.1 : 0.18,
    railColor: cssColorToHex(cssVar('--gear-rail', gear ? '#c8cad4' : '#c8cad4')),
    wallBrass: cssColorToHex(cssVar('--gear-wall-brass', gear ? '#a8845a' : '#a8845a')),
    wallSteel: cssColorToHex(cssVar('--gear-wall-steel', gear ? '#9090a0' : '#9090a0')),
    coreColor: gear ? 0xd8c8a8 : 0xc8d0ff,
    coreOpacity: 0.48,
    coreGlowColor: gear ? 0xe8c888 : 0xa8b8ff,
  };
}
